/**
 * Invoice API Wrapper
 *
 * Provides methods for endpoints not yet included in the generated OpenAPI client.
 * Once `pnpm run api:generate` includes these endpoints, migrate callers to use
 * `invoiceApi` directly and remove this file.
 *
 * TO REMOVE THIS FILE:
 * 1. Run: pnpm run api:generate
 * 2. Verify the following methods exist in InvoiceApi:
 *    - apiVversionInvoiceIdPayPost
 *    - apiVversionInvoiceIdMatchingLedgerEntriesGet
 *    - apiVversionInvoiceIdLinkLedgerEntryPost
 * 3. Remove this file and update callers to use invoiceApi directly.
 */

import { InvoiceApi } from 'axiosApi/apis/invoice-api';
import { setApiKeyToObject } from 'axiosApi/common';
import { AxiosResponse } from 'axios';

export interface PayInvoiceRequest {
  amount: number;      // integer, already × 100
  incoming: number;    // 0 = outgoing (debit), 1 = incoming (credit)
  idAccount: number;
  description: string;
  date: Date;
}

export interface MatchingLedgerEntry {
  id: number;
  amount: number;       // integer × 100
  incoming: number;
  date: string;         // ISO date string
  description: string;
  account?: { id: number; name: string; accountNumber: string };
}

export class InvoiceApiWrapper {
  private invoiceApi: InvoiceApi;

  constructor(invoiceApi: InvoiceApi) {
    this.invoiceApi = invoiceApi;
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const configuration = (this.invoiceApi as any).configuration;
    const headers: Record<string, string> = {};
    await setApiKeyToObject(headers, 'Userentity', configuration);
    await setApiKeyToObject(headers, 'Referer', configuration);
    await setApiKeyToObject(headers, 'Authorization', configuration);
    return headers;
  }

  /**
   * Atomically marks an invoice as Paid:
   * creates an AccountingSeat, links it to the invoice, and
   * sets the workflow state to Paid (118) — all in one transaction.
   *
   * POST /api/v{version}/Invoice/{id}/pay
   */
  async payInvoice(
    version: string,
    id: number,
    body: PayInvoiceRequest,
    options?: any
  ): Promise<AxiosResponse<any>> {
    // Check if the method exists in the generated client (after api:generate)
    if ('apiVversionInvoiceIdPayPost' in this.invoiceApi) {
      return (this.invoiceApi as any).apiVversionInvoiceIdPayPost(version, id, body, options);
    }

    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/${id}/pay`,
      JSON.stringify(body),
      { headers, ...options }
    );
  }

  /**
   * Returns existing ledger entries (AccountingSeat) whose amount matches
   * the given value and whose date falls within the same month up to that date.
   *
   * GET /api/v{version}/Invoice/{id}/matching-ledger-entries?amount={amount}&date={date}
   */
  async getMatchingLedgerEntries(
    version: string,
    invoiceId: number,
    amount: number,   // integer × 100
    date: Date,
    options?: any
  ): Promise<AxiosResponse<{ data: MatchingLedgerEntry[] }>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    const dateStr = date.toISOString().split('T')[0];

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/matching-ledger-entries`,
      { params: { amount, date: dateStr }, headers, ...options }
    );
  }

  /**
   * Links an already-existing AccountingSeat to the invoice and transitions it
   * to the Paid state — without creating a new accounting seat row.
   *
   * POST /api/v{version}/Invoice/{id}/link-ledger-entry
   */
  async linkExistingLedgerEntry(
    version: string,
    invoiceId: number,
    accountingSeatId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/link-ledger-entry`,
      JSON.stringify({ accountingSeatId }),
      { headers, ...options }
    );
  }

  /**
   * Updates the workflow state of a DynamoDB invoice (module 5, identified by jobId).
   * Used when no MySQL entity exists yet.
   *
   * POST /api/v{version}/Invoice/job/{jobId}/workflow-state
   */
  async updateJobInvoiceWorkflowState(
    version: string,
    jobId: string,
    workflowStateId: number,
    comments?: string,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/job/${jobId}/workflow-state`,
      JSON.stringify({ workflowStateId, comments }),
      { headers, ...options }
    );
  }
}
